import { Divider } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CardContent from '@material-ui/core/CardContent';
import { DateRangeRounded, DescriptionRounded, EventBusyRounded, HomeWorkRounded, MailRounded, PhoneRounded, RoomRounded, RouterRounded, SchoolRounded, WorkRounded } from '@material-ui/icons';
import { useStyles } from '@webapps/styles';
import { useEffect, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { formatPhoneNumber } from 'react-phone-number-input';
import { getAbsenceHoursByStudentIdAndClassroomId } from '@webapps/business';
import { useQuery } from '@webapps/reactjs-tech-components';

import AvatarStudent from 'components/Avatar/AvatarStudent';
import RecordSidebar from './RecordSidebar';
import RecordCardSkeleton from './RecordCardSkeleton';
import { getContractStatusChip, pluralize, roundToPreviousHalf } from '@webapps/utils';

const RecordCard = ({ info, toOpen = false, setSelectedRecord, isLoading = false }) => {
  const {
    classes,
    css,
    global,
    cx,
    theme: { spacing },
  } = useStyles(styles);

  const { data: absenceData = {} } = useQuery(getAbsenceHoursByStudentIdAndClassroomId, {
    key: ['absenceHoursByStudentIdAndClassroomId', info?.individual?.id, info?.individual?.classroom?.id, "SCHOOL"],
    variables: { studentId: info?.individual?.id, classId: info?.individual?.classroom?.id, type: 'SCHOOL' },
    enabled: !!info?.individual?.classroom?.id && !isLoading
  });

  useEffect(() => {
    if (isLoading) return;

    if (toOpen) {
      setSelectedRecord(info);
    }
  }, [toOpen, isLoading]);

  if (isLoading) {
    return <RecordCardSkeleton />;
  }

  return (
    <div className={classes.container}>
      <CardContent className={css({ padding: spacing(0, 0, 2.5, 0) })}>
        <div className={css({ display: 'flex', justifyContent: 'space-between' })}>
          <div className={classes.photo}>
            <div>
              <AvatarStudent className={classes.avatar} applicant={info?.individual} />
            </div>
            <div>
              <div className={cx(global.title, classes.name, css({ marginBottom: "10px" }))}>
                {info?.individual?.firstname} {info?.individual?.lastname}
              </div>
              <div className={cx(classes.name, global.infoText, global.frac)} >
                <RoomRounded className={global.smallIcon} />
                <div>{info?.individual?.address?.cityCode} {info?.individual?.address?.city}</div>
              </div>
            </div>
          </div>
        </div>
        {getContractStatusChip(info)}
        <Divider className={classes.divider} />
        <div className={cx(global.infoText, classes.address)}>
          <RouterRounded className={global.infoIcon} />
          <div>{`OPCO : ${info?.opco || info?.businessEmployer?.opco || 'Non renseigné'}`}</div>
        </div>
        <div className={cx(global.infoText, classes.address)}>
          <DescriptionRounded className={global.infoIcon} />
          <div>{info?.training?.trainingType?.label}</div>
        </div>
        <div className={cx(global.infoText, classes.address)}>
          <WorkRounded className={global.infoIcon} />
          <div>{info?.training?.label}</div>
        </div>
        <div className={cx(global.infoText, classes.address)}>
          <SchoolRounded className={global.infoIcon} />
          <div>{`${info?.training?.degree?.label} (Code diplôme ${info?.training?.diplomaCode})`}</div>
        </div>
        <div className={cx(global.infoText, classes.address)}>
          <HomeWorkRounded className={global.infoIcon} />
          <div>{`O.F : ${info?.training?.school?.name}`}</div>
        </div>
        <div className={cx(global.infoText, classes.address)}>
          <DateRangeRounded className={global.infoIcon} />
          Contrat :{' '}
          <FormattedDate
            day="numeric"
            month="numeric"
            value={info?.businessContract?.executionDate}
            year="numeric"
          />{' '}
          au{' '}
          <FormattedDate
            day="numeric"
            month="numeric"
            value={info?.businessContract?.endDate}
            year="numeric"
          />
        </div>
        <Divider className={classes.divider} />
        <div className={cx(global.infoText, classes.address)}>
          <MailRounded className={global.infoIcon} />
          <div>{info?.individual?.email}</div>
        </div>
        <div className={cx(global.infoText, classes.address)}>
          <PhoneRounded className={global.infoIcon} />
          <div>{info?.individual?.phone ? formatPhoneNumber(info?.individual?.phone) : 'Non renseigné'}</div>
        </div>
        <div className={cx(global.infoText, classes.address)}>
          <EventBusyRounded className={global.infoIcon} />
          <div> {!absenceData?.school ? 'Pas d\'absence' : `${roundToPreviousHalf(absenceData?.school / 7)} ${pluralize("jour", roundToPreviousHalf(absenceData?.school / 7))}  (${absenceData?.school} ${pluralize("heure", absenceData?.school)} d'absence)`} </div>
        </div>
      </CardContent>
      <div className={classes.buttons}>
        <Button
          onClick={() => setSelectedRecord(info)}
          className={global.greyButton}
          variant="outlined"
        >
          Voir les détails
        </Button>
      </div>
    </div>
  );
};

const styles = ({ spacing }) => ({
  container: {
    backgroundColor: "white",
    borderRadius: 16,
    padding: spacing(2.5),
  },
  buttonsContainer: {
    display: 'flex',
    columnGap: 10,
  },
  name: {
    marginLeft: spacing(2),
    display: 'flex',
    textAlign: 'start',
    fontFamily: 'Montserrat',
    wordBreak: 'break-all',
  },
  divider: {
    margin: "16px 0px",
  },
  address: {
    display: 'flex',
    marginTop: spacing(0.5),
    textAlign: 'start',
    alignItems: 'center',
  },
  avatar: {
    height: 60,
    width: 60,
    border: '1px solid #C0D9FF',
  },
  photo: {
    marginBottom: spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    rowGap: 10,
  },
});

export default RecordCard;
